/**
 * Cover Studio — text-overlay style library (marketing-site port).
 * Same library as ms-studio's real Cover Studio (/root/ms-studio/src/lib/covers/text-styles.ts),
 * itself ported from the Love's Echo cover editor (kimballandvitaly.com/echo/book-covers-editor.html) —
 * 6 typography families, 4 placement zones, 6-color palette, text-background scrim control.
 * Pure client-side CSS overlay on text-free art — $0, instant, no regeneration needed.
 */

export const DEFAULT_TEXT_STYLE = {
  zone: 'bottom',
  style: 'elegant',
  titleColor: '#ffffff',
  subtitleColor: '#ffffff',
  authorColor: '#ffffff',
  scrimColor: '#000000',
  scrimOpacity: 55,
};

export const TEXT_ZONES = [
  { value: 'top', label: 'Top band' },
  { value: 'bottom', label: 'Bottom banner' },
  { value: 'center', label: 'Center overlay' },
  { value: 'left', label: 'Left panel' },
];

export const TEXT_COLORS = [
  { value: '#ffffff', label: 'White' },
  { value: '#f3ead9', label: 'Warm cream' },
  { value: '#2a2420', label: 'Ink black' },
  { value: '#c9a96e', label: 'Soft gold' },
  { value: '#8a4636', label: 'Terracotta' },
  { value: '#e08a6f', label: 'Sunset coral' },
];

export const SCRIM_COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#1a1a2e', label: 'Deep navy' },
  { value: '#2a2420', label: 'Warm dark' },
  { value: '#ffffff', label: 'White' },
];

// Same 6 families as the reference editor's data-style presets.
export const TEXT_STYLES = {
  elegant: {
    label: 'Elegant Serif',
    titleFont: "'Lora', Georgia, serif", titleWeight: 700,
    subtitleFont: "'Lora', Georgia, serif", subtitleStyle: 'italic',
  },
  classic: {
    label: 'Classic Literary',
    titleFont: "'Libre Baskerville', Georgia, serif", titleWeight: 400,
    subtitleFont: "'Outfit', ui-sans-serif, sans-serif",
  },
  bold: {
    label: 'Bold Modern',
    titleFont: "'Work Sans', ui-sans-serif, sans-serif", titleWeight: 700,
    subtitleFont: "'Outfit', ui-sans-serif, sans-serif",
  },
  cinematic: {
    label: 'Cinematic Display',
    titleFont: "'Big Shoulders Display', ui-sans-serif, sans-serif", titleWeight: 700,
    titleTransform: 'uppercase', titleLetterSpacing: '.02em',
    subtitleFont: "'Outfit', ui-sans-serif, sans-serif",
  },
  modern: {
    label: 'Modern Display',
    titleFont: "'Gloock', Georgia, serif", titleWeight: 400, titleLetterSpacing: '.01em',
    subtitleFont: "'Outfit', ui-sans-serif, sans-serif",
  },
  refined: {
    label: 'Refined Italic',
    titleFont: "'Italiana', Georgia, serif", titleWeight: 400, titleLetterSpacing: '.03em',
    subtitleFont: "'Lora', Georgia, serif", subtitleStyle: 'italic',
  },
};

export const TEXT_STYLE_OPTIONS = Object.entries(TEXT_STYLES).map(([value, def]) => ({ value, label: def.label }));

/** Zone → flex layout for the text block, matching the reference editor's 4 placements. */
export function zoneLayout(zone) {
  switch (zone) {
    case 'top': return { justify: 'flex-start', align: 'center', textAlign: 'center' };
    case 'center': return { justify: 'center', align: 'center', textAlign: 'center' };
    case 'left': return { justify: 'center', align: 'flex-start', textAlign: 'left' };
    case 'bottom':
    default: return { justify: 'flex-end', align: 'center', textAlign: 'center' };
  }
}

export function hexToRgba(hex, alpha) {
  const n = hex.replace('#', '');
  if (n.length !== 6) return `rgba(0,0,0,${alpha})`;
  const num = parseInt(n, 16);
  return `rgba(${(num >> 16) & 0xff},${(num >> 8) & 0xff},${num & 0xff},${alpha})`;
}

export function scrimStyleFor(zone, rgba) {
  const dir = zone === 'top' ? 'to bottom' : zone === 'left' ? 'to right' : 'to top';
  return { background: `linear-gradient(${dir}, ${rgba} 0%, ${rgba.replace(/[\d.]+\)$/, '0)')} 65%)` };
}
